import type { FtueStep } from './types'

/** Action-driven steps necessarily need their anchor; all others are inert by default. */
export function allowsAnchorInteraction(step: FtueStep): boolean {
  return (
    step.allowAnchorInteraction === true ||
    step.dismissOnAction === true ||
    step.advanceOnSpotlightClick === true ||
    step.exitTourOnAnchorClick === true
  )
}

function eventPath(event: Event): EventTarget[] {
  if (typeof event.composedPath === 'function') return event.composedPath()
  return event.target ? [event.target] : []
}

function isCoachmarkControl(path: EventTarget[]): boolean {
  return path.some(
    (target) => target instanceof Element && target.hasAttribute('data-ftue-coachmark'),
  )
}

function isInsideAnchor(path: EventTarget[], anchor: HTMLElement): boolean {
  return path.some(
    (target) => target instanceof Node && (target === anchor || anchor.contains(target)),
  )
}

/**
 * Radix and similar controls render their popup under `body`. Treat only a popup
 * explicitly owned by a control inside the active anchor as part of the allowed
 * action; unrelated, already-open portals stay blocked.
 */
function isInsideControlledPortal(path: EventTarget[], anchor: HTMLElement): boolean {
  const controlledIds = new Set<string>()
  const controllers = [anchor, ...anchor.querySelectorAll<HTMLElement>('[aria-controls]')]
  for (const controller of controllers) {
    for (const id of (controller.getAttribute('aria-controls') ?? '').split(/\s+/)) {
      if (id) controlledIds.add(id)
    }
  }
  if (controlledIds.size === 0) return false
  return path.some(
    (target) => target instanceof HTMLElement && !!target.id && controlledIds.has(target.id),
  )
}

export function isFtueInteractionAllowed(
  event: Event,
  step: FtueStep,
  anchor: HTMLElement,
): boolean {
  const path = eventPath(event)
  if (isCoachmarkControl(path)) return true
  if (!allowsAnchorInteraction(step)) return false
  return isInsideAnchor(path, anchor) || isInsideControlledPortal(path, anchor)
}
